<script setup lang="ts">
import { useContextsStore } from './stores/contexts'
import type { Locale } from '@nuxt/ui'

const { locales, locale, setLocale } = useI18n()

const appStore = useAppStore()
const contextsStore = useContextsStore()
const currentContext = computed(() => contextsStore.currentContext)

const { isSyncing, isBootstrapping } = useSync()

const { hasAccepted, consentStatus } = useCookieConsent()
const colorMode = useColorMode()

const LOCALE_STORAGE_KEY = 'minimind_locale'

function clearPreferences() {
  localStorage.removeItem('nuxt-color-mode')
  localStorage.removeItem(LOCALE_STORAGE_KEY)
}

// Restore saved locale on mount if consent was previously given
onMounted(async () => {
  if (hasAccepted.value) {
    const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (savedLocale && savedLocale !== locale.value) {
      await setLocale(savedLocale as 'en-GB' | 'es-ES')
    }
  }
})

// When consent is declined, clear all stored preferences
watch(consentStatus, (status) => {
  if (status === 'declined') clearPreferences()
})

// Persist color mode preference only when accepted
watch(() => colorMode.preference, () => {
  if (!hasAccepted.value) localStorage.removeItem('nuxt-color-mode')
})

// Persist locale preference only when accepted
watch(locale, (newLocale) => {
  if (hasAccepted.value) {
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)
  } else {
    localStorage.removeItem(LOCALE_STORAGE_KEY)
  }
})

/**
 * Request persistent storage permission on app startup
 */
async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    // Check if storage is already persistent
    const isPersisted = await navigator.storage.persisted()

    if (isPersisted) {
      return
    }

    // Request persistent storage
    await navigator.storage.persist()
  }
}

onMounted(() => {
  requestPersistentStorage()
})

/**
 * Applies the given color to the UI as the primary color.
 * Does so by updating the '--ui-primary' CSS variable.
 * @param {string} color - a valid CSS color value
 */
function applyTheme(color: string) {
  // Update CSS Variable
  document.documentElement.style.setProperty(
    '--ui-primary',
    `var(--color-context-${color})`
  )
}

watch(
  currentContext,
  () => {
    if (!currentContext.value) return
    applyTheme(currentContext.value.color)
  },
  { immediate: true }
)

useDynamicFavicon()
</script>

<template>
  <UApp :locale="locales[locale as keyof typeof locales] as Locale<unknown>">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <NuxtRouteAnnouncer />
    <NuxtLoadingIndicator color="var(--ui-primary)" :height="4" />
    <ContextSelector />
    <ConfirmationDialog />
    <DonationReminder />
    <ContextSwitcher />
    <OnboardingPanel v-if="appStore.isOnBoardingActive" />
    <Transition name="fade">
      <div
        v-if="isBootstrapping && !currentContext"
        class="fixed z-50 inset-0 bg-default/80 backdrop-blur-xl grid place-items-center"
      >
        <div class="text-center">
          <UIcon
            name="i-mingcute-loading-line"
            class="animate-spin text-4xl mb-4 mx-auto text-primary"
          />
          <p class="text-lg font-medium">
            {{ $t('sync.syncing_data') }}
          </p>
        </div>
      </div>
    </Transition>
    <CookieConsent />
    <Transition appear name="slide-up">
      <div
        v-if="isSyncing"
        class="fixed bottom-4 left-2 right-2 text-xs z-50 grid place-items-center pointer-events-none"
      >
        <div
          class="p-2 mx-auto bg-default shadow-lg rounded-3xl flex items-center justify-center gap-2"
        >
          <UIcon name="i-mingcute-loading-line" class="animate-spin" />
          {{ $t('sync.syncing') }}
        </div>
      </div>
    </Transition>
  </UApp>
</template>
